import { Box, Card, CardContent, CardHeader, GridSize, Stack, Typography } from '@mui/material';
import { ResponsiveStyleValue } from '@mui/system';
import { ReactNode, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSystemConfig } from '../../hooks/useSystemConfig';
import { useTheme } from '../../hooks/useTheme';
import { systemConfigKeyEnum } from '../../utils/enums';
import { formatNumber } from '../../utils/numbers';
import Icon, { IconProps } from './Icon';
import IconButton from './IconButton';

interface SectionCardProps {
    title: string;
    iconTitleName?: IconProps['name'];
    children?: ReactNode;
    isSelected?: boolean;
    color?: string;
    iconTitleColor?: string;
    onAdd?: () => void;
    extraActions?: ReactNode;
    disabled?: boolean;
    subTitle?: string;
    size?: ResponsiveStyleValue<GridSize>;
    totalPrice?: number;
    hideTotalPrice?: boolean;
}

const SectionCard = forwardRef<HTMLDivElement, SectionCardProps>(
    (
        {
            title,
            children,
            isSelected = false,
            color = '#1976d2',
            iconTitleColor,
            iconTitleName,
            onAdd,
            extraActions,
            disabled = false,
            subTitle,
            size,
            totalPrice,
            hideTotalPrice,
        },
        ref
    ) => {
        const { muiTheme } = useTheme();
        const { getConfigValue } = useSystemConfig();
        const { t } = useTranslation();

        return (
            <Box ref={ref} sx={{ mb: 0.5 }}>
                <Card
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        borderRadius: {xs: 0, md: 1}
                    }}
                >
                    <CardHeader
                        sx={{ p: 1 }}
                        title={
                            <Stack direction="row" spacing={0.5} alignItems="center">
                                {iconTitleName && <Icon name={iconTitleName} color="secondary" sx={{ color: iconTitleColor }} />}
                                <Typography variant="h6" component="div">
                                    {title}
                                </Typography>
                            </Stack>
                        }
                        subheader={
                            subTitle ? (
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                    {subTitle}
                                </Typography>
                            ) : null
                        }
                        action={
                            <Stack direction="row" spacing={1} px={0.5} alignItems="center">
                                {!disabled && extraActions}
                                {!hideTotalPrice && (
                                    <Box
                                        sx={{
                                            bgcolor: muiTheme.palette.warning.main,
                                            color: muiTheme.palette.warning.contrastText,
                                            px: 1.5,
                                            borderRadius: 1,
                                            fontWeight: 600,
                                            minWidth: 50,
                                            textAlign: 'center',
                                            height: 25,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'flex-end',
                                        }}
                                    >
                                        {totalPrice !== undefined ? formatNumber(totalPrice, t('locale')) : '-'}{' '}
                                        {getConfigValue(systemConfigKeyEnum.CURRENCY_SYMBOL)}
                                    </Box>
                                )}

                                {!disabled && onAdd && (
                                    <IconButton
                                        iconName="addCircle"
                                        onClick={() => {
                                            onAdd?.();
                                        }}
                                    />
                                )}
                            </Stack>
                        }
                    />
                    {children && (
                        <CardContent
                            sx={{
                                overflowY: 'auto',
                                display: 'flex',
                                flexDirection: 'column',
                                flex: 1,
                                p: 1,
                                pt: 0,
                                '&:last-child': { pb: 1 },
                            }}
                        >
                            {children}
                        </CardContent>
                    )}
                </Card>
            </Box>
        );
    }
);

export default SectionCard;
